import React from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Modal,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import StatusSelect from '../components/StatusSelect';
import EntityCommentsCard from '../components/EntityCommentsCard';
import LeadFilesTab from '../components/LeadFilesTab';
import {
  getInquiryStatusTone,
  getOfferStatusTone,
  getOrderStatusTone,
  getQuoteStatusTone,
  OFFER_STATUSES,
  ORDER_STATUSES,
  PRIORITY_TONES,
  TYPE_TONES,
  DEFAULT_TONE,
} from '../constants/statuses';
import { useI18n } from '../i18n';
import { formatDate, formatDateTime } from '../utils/formatters';
import { getLeadInquiries } from '../utils/leadProfileHelpers';
import { brandSemantic } from '../config/brand';

const { confirm } = Modal;

export default function LeadProfilePage({
  selectedLead,
  leadProfileReturnView,
  leadProfileActiveTab,
  setLeadProfileActiveTab,
  contacts,
  contactPersons,
  offers,
  inquiries = [],
  quotes = [],
  files,
  showModal,
  setSelectedLead,
  setCurrentView,
  openOrderDetails,
  openQuoteDetail,
  openInquiryDetail,
  onCreateInquiry,
  getOfferSupplierSections,
  getFollowUpsForOffer,
  getOverdueCount,
  getOfferStatus,
  updateOfferStatus,
  getLatestComment,
  openCommentsModal,
  handleUploadFile,
  handleDeleteFile,
  handleEditFileDescription,
  handlePreviewPDF,
  handleDownloadFile,
  canDelete = true,
}) {
  const { t } = useI18n();
  if (!selectedLead) return null;

  const leadContacts = contacts.filter((contact) => contact.clientId === selectedLead.id);
  const leadContactPersons = contactPersons.filter((person) => person.entityId === selectedLead.id);
  const leadOffers = offers.filter((offer) => offer.clientId === selectedLead.id);
  const leadInternalQuotes = quotes.filter((quote) => quote.clientId === selectedLead.id);
  const leadSalesQuotes = leadOffers.filter((offer) => offer.type === 'Offer');
  const leadOrders = leadOffers.filter((offer) => offer.type === 'Order');
  const leadQuotesTabData = [...leadInternalQuotes, ...leadSalesQuotes].sort((a, b) => (
    new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
  ));
  const leadInquiries = getLeadInquiries(inquiries, selectedLead.id);

  const contactPersonColumns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('Position'),
      dataIndex: 'position',
      key: 'position',
      render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Email'),
      dataIndex: 'email',
      key: 'email',
      render: (value) => value ? <a href={`mailto:${value}`}>{value}</a> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (value) => value ? <a href={`tel:${value}`}>{value}</a> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          style={{ padding: 0, height: 'auto' }}
          onClick={() => showModal('editContactPerson', record)}
        >
          {t('Edit')}
        </Button>
      ),
    },
  ];

  const contactColumns = [
    {
      title: t('Contact Date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a, b) => new Date(b.date) - new Date(a.date),
      defaultSortOrder: 'ascend',
      render: (date) => formatDate(date),
    },
    {
      title: t('Contact Source'),
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type) => {
        const colors = { Phone: 'blue', Email: 'green', Network: 'cyan', 'Fair/Event': 'gold', Referral: 'purple', Other: 'orange' };
        return <Tag color={colors[type]}>{t(type)}</Tag>;
      },
    },
    {
      title: t('Subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (text, record) => (
        <Button type="link" onClick={() => showModal('viewContact', record)} style={{ padding: 0 }}>
          {text}
        </Button>
      ),
    },
    {
      title: t('Contact By'),
      dataIndex: 'userName',
      key: 'userName',
      width: 150,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => showModal('editContact', record)}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete Contact Record'),
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() { message.success(t('Contact record deleted')); },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const quoteRecordColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 135,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => (
            record.type === 'Quote'
              ? openQuoteDetail(record)
              : openOrderDetails(record, 'leadProfile', { leadProfileTab: '3' })
          )}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 110,
      render: (type) => (
        <SoftTag tone={TYPE_TONES[type] || DEFAULT_TONE}>
          {t(type === 'Quote' ? 'Internal Quote' : 'Sales Quote')}
        </SoftTag>
      ),
    },
    {
      title: t('Suppliers'),
      key: 'suppliers',
      width: 220,
      render: (_, record) => {
        if (record.type === 'Quote') {
          const supplierNames = (record.suppliers || []).map((supplier) => supplier.supplierName).filter(Boolean);
          if (supplierNames.length === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
          return (
            <Space wrap size={4}>
              {supplierNames.map((name) => <Tag key={name} style={{ fontSize: 11, margin: 0 }}>{name}</Tag>)}
            </Space>
          );
        }
        const supplierSections = getOfferSupplierSections(record.id);
        if (!supplierSections || supplierSections.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        }
        return (
          <Space direction="vertical" size={2}>
            <Space wrap size={4}>
              {supplierSections.map((supplier) => (
                <Tag key={supplier.name} style={{ fontSize: 11, margin: 0 }}>{supplier.name}</Tag>
              ))}
            </Space>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
              onClick={() => showModal('viewSuppliers', record)}
            >
              {t('View details')}
            </Button>
          </Space>
        );
      },
    },
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (value) => (value ? <span style={{ fontWeight: 500 }}>{value}</span> : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Follow-ups'),
      key: 'followUps',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const items = getFollowUpsForOffer(record.id);
        const overdue = getOverdueCount(record.id);
        if (items.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ padding: 0 }}
              onClick={() => showModal('viewFollowUps', record, { startAdding: true })}
            >
              {t('Add')}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => showModal('viewFollowUps', record)}
          >
            <Space size={4} align="center">
              <BellOutlined style={{ color: brandSemantic.primary, fontSize: 14, lineHeight: 1 }} />
              <span style={{ fontWeight: 600 }}>{items.length}</span>
              {overdue > 0 && (
                <SoftTag
                  tone={{ color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder }}
                  style={{ minHeight: 20, lineHeight: '18px', padding: '0 6px', fontSize: 11 }}
                >
                  !{overdue}
                </SoftTag>
              )}
            </Space>
          </Button>
        );
      },
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        if (record.type === 'Quote') {
          return <SoftTag tone={getQuoteStatusTone(status)}>{t(status)}</SoftTag>;
        }
        const current = getOfferStatus(record.id, status);
        return (
          <StatusSelect
            value={current}
            onChange={(value) => updateOfferStatus(record.id, value)}
            statuses={OFFER_STATUSES}
            getTone={getOfferStatusTone}
            width={Math.min(Math.max(current.length * 7 + 34, 120), 260)}
          />
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => {
        if (record.type === 'Quote') {
          return (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => openQuoteDetail(record)}
            >
              {t('Open')}
            </Button>
          );
        }
        return (
          <Space direction="vertical" size={2}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => showModal('editOffer', record)}
            >
              {t('Edit')}
            </Button>
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ padding: 0, height: 'auto' }}
                onClick={() => {
                  confirm({
                    title: t('Delete'),
                    content: t('Are you sure you want to delete this entry?'),
                    onOk() { message.success(t('Deleted')); },
                  });
                }}
              >
                {t('Delete')}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  const orderColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 135,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => openOrderDetails(record, 'leadProfile', { leadProfileTab: '4' })}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Suppliers'),
      key: 'suppliers',
      width: 220,
      render: (_, record) => {
        const supplierSections = getOfferSupplierSections(record.id);
        if (!supplierSections || supplierSections.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>—</span>;
        }
        return (
          <Space direction="vertical" size={2}>
            <Space wrap size={4}>
              {supplierSections.map((supplier) => (
                <Tag key={supplier.name} style={{ fontSize: 11, margin: 0 }}>{supplier.name}</Tag>
              ))}
            </Space>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
              onClick={() => showModal('viewSuppliers', record)}
            >
              {t('View details')}
            </Button>
          </Space>
        );
      },
    },
    {
      title: t('Amount'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>,
    },
    {
      title: t('Follow-ups'),
      key: 'followUps',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const items = getFollowUpsForOffer(record.id);
        const overdue = getOverdueCount(record.id);
        if (items.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ padding: 0 }}
              onClick={() => showModal('viewFollowUps', record, { startAdding: true })}
            >
              {t('Add')}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => showModal('viewFollowUps', record)}
          >
            <Space size={4} align="center">
              <BellOutlined style={{ color: brandSemantic.primary, fontSize: 14, lineHeight: 1 }} />
              <span style={{ fontWeight: 600 }}>{items.length}</span>
              {overdue > 0 && (
                <SoftTag
                  tone={{ color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder }}
                  style={{ minHeight: 20, lineHeight: '18px', padding: '0 6px', fontSize: 11 }}
                >
                  !{overdue}
                </SoftTag>
              )}
            </Space>
          </Button>
        );
      },
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (status, record) => {
        const current = getOfferStatus(record.id, status);
        return (
          <StatusSelect
            value={current}
            onChange={(value) => updateOfferStatus(record.id, value)}
            statuses={ORDER_STATUSES}
            getTone={getOrderStatusTone}
            width={Math.min(Math.max(current.length * 7 + 34, 120), 260)}
          />
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => showModal('editOffer', record)}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                confirm({
                  title: t('Delete'),
                  content: t('Are you sure you want to delete this entry?'),
                  onOk() { message.success(t('Deleted')); },
                });
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const inquiryColumns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 170,
      render: (id, record) => (
        <Button
          type="link"
          onClick={() => openInquiryDetail?.(record)}
          style={{ padding: 0, fontWeight: 600 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 110,
      render: (priority) => (
        <SoftTag tone={PRIORITY_TONES[priority] || PRIORITY_TONES.Medium}>
          {t(priority)}
        </SoftTag>
      ),
    },
    {
      title: t('Items'),
      key: 'items',
      width: 80,
      align: 'right',
      render: (_, record) => record.items?.length || 0,
    },
    {
      title: t('Deadline'),
      dataIndex: 'neededBefore',
      key: 'neededBefore',
      width: 140,
      render: (date) => date ? formatDate(date) : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status) => <SoftTag tone={getInquiryStatusTone(status)}>{t(status)}</SoftTag>,
    },
    {
      title: t('Open'),
      key: 'open',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => openInquiryDetail?.(record)}
        >
          {t('Open')}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Button
        icon={<HomeOutlined />}
        onClick={() => {
          setSelectedLead(null);
          setCurrentView(leadProfileReturnView);
        }}
        style={{ marginBottom: '16px' }}
      >
        {t('Back to List')}
      </Button>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={24}>
          <Col span={12}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t('Client Name')}>
                <strong>{selectedLead.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="CVR">{selectedLead.cvr}</Descriptions.Item>
              <Descriptions.Item label={t('Client Type')}>
                <Tag color={selectedLead.status === 'Customer' ? 'green' : 'blue'}>
                  {t(selectedLead.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('Created')}>{formatDate(selectedLead.created)}</Descriptions.Item>
              <Descriptions.Item label={t('Address')}>{selectedLead.address}</Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label={t('Email')}>
                <a href={`mailto:${selectedLead.email}`}>{selectedLead.email}</a>
              </Descriptions.Item>
              <Descriptions.Item label={t('Phone')}>
                <a href={`tel:${selectedLead.phone}`}>{selectedLead.phone}</a>
              </Descriptions.Item>
            </Descriptions>

            <Divider style={{ margin: '16px 0' }} />

            <EntityCommentsCard
              entityId={selectedLead.id}
              entityName={selectedLead.name}
              getLatestComment={getLatestComment}
              formatDateTime={formatDateTime}
              onOpenComments={openCommentsModal}
            />
          </Col>
        </Row>
      </Card>

      <Tabs activeKey={leadProfileActiveTab} onChange={setLeadProfileActiveTab}>
        <Tabs.TabPane tab={t('Contact Records')} key="0">
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ padding: 0, marginBottom: 12 }}
            onClick={() => showModal('createContact')}
          >
            {t('Add Contact Record')}
          </Button>
          <Table columns={contactColumns} dataSource={leadContacts} pagination={false} bordered size="middle" />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('Contact Persons')} key="1">
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ padding: 0, marginBottom: 12 }}
            onClick={() => showModal('createContactPerson')}
          >
            {t('Add Contact Person')}
          </Button>
          <Table columns={contactPersonColumns} dataSource={leadContactPersons} rowKey="id" pagination={false} bordered size="middle" />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('Inquiries')} key="2">
          <Button
            type="link"
            icon={<PlusOutlined />}
            style={{ padding: 0, marginBottom: 12 }}
            onClick={onCreateInquiry}
          >
            {t('Add New Inquiry')}
          </Button>
          {leadInquiries.length === 0 ? (
            <Empty description={t('No inquiries yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              columns={inquiryColumns}
              dataSource={leadInquiries}
              rowKey="id"
              pagination={false}
              bordered
              size="middle"
            />
          )}
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('Quotes')} key="3">
          <Table columns={quoteRecordColumns} dataSource={leadQuotesTabData} rowKey="id" pagination={false} bordered size="middle" />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('Orders')} key="4">
          <Table columns={orderColumns} dataSource={leadOrders} rowKey="id" pagination={false} bordered size="middle" />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('Files')} key="5">
          <LeadFilesTab
            clientId={selectedLead.id}
            files={files}
            onUploadFile={handleUploadFile}
            onDeleteFile={handleDeleteFile}
            onEditFileDescription={handleEditFileDescription}
            onPreviewPDF={handlePreviewPDF}
            onDownloadFile={handleDownloadFile}
            canDelete={canDelete}
          />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
